import { motion } from 'framer-motion';
import { Check, Lock } from 'lucide-react';
import { cn } from '../../lib/cn';
import { STELLAR_ACCENT_KEY } from '../../lib/theme';
import {
  accentPalette,
  springQuick,
  stellarAccent,
  tapScale,
  type AccentKey,
} from '../../tokens';

const ACCENT_LABELS: Record<AccentKey, string> = {
  blue: 'Blue',
  indigo: 'Indigo',
  teal: 'Teal',
  mint: 'Mint',
  green: 'Green',
  yellow: 'Yellow',
  orange: 'Orange',
  red: 'Red',
  pink: 'Pink',
  purple: 'Purple',
  brown: 'Brown',
};

// Ordered around the color wheel for a calm rainbow read.
const ORDER: AccentKey[] = [
  'blue',
  'indigo',
  'teal',
  'mint',
  'green',
  'yellow',
  'orange',
  'red',
  'pink',
  'purple',
  'brown',
];

type Props = {
  value: string;
  onChange: (key: string) => void;
  /** When false, the Stellar swatch shows a lock badge instead of the color. */
  stellarUnlocked?: boolean;
  /** Tap handler when the user taps the locked Stellar swatch. */
  onStellarLockedTap?: () => void;
};

/**
 * 12 accent swatches: 11 standard colors arranged around the wheel +
 * Stellar (the rate-prompt unlock). Stellar is greyed with a Lock icon
 * when not yet unlocked. Selection is communicated via a checkmark, not
 * a border. Rendered as a 6×2 grid.
 */
export function AccentPicker({
  value,
  onChange,
  stellarUnlocked = false,
  onStellarLockedTap,
}: Props) {
  return (
    <div className="grid grid-cols-6 gap-2">
      {ORDER.map((key) => {
        const palette = accentPalette[key];
        const active = value === key;
        return (
          <Swatch
            key={key}
            label={ACCENT_LABELS[key]}
            color={`rgb(${palette.light})`}
            active={active}
            onClick={() => onChange(key)}
          />
        );
      })}
      <StellarSwatch
        active={value === STELLAR_ACCENT_KEY}
        unlocked={stellarUnlocked}
        onClick={() => {
          if (stellarUnlocked) {
            onChange(STELLAR_ACCENT_KEY);
          } else {
            onStellarLockedTap?.();
          }
        }}
      />
    </div>
  );
}

function Swatch({
  label,
  color,
  active,
  onClick,
}: {
  label: string;
  color: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={tapScale}
      transition={springQuick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'aspect-square rounded-pill inline-flex items-center justify-center',
        'shadow-sm',
      )}
      style={{ backgroundColor: color }}
    >
      {active ? (
        <Check size={14} strokeWidth={3} className="text-white" />
      ) : null}
    </motion.button>
  );
}

function StellarSwatch({
  active,
  unlocked,
  onClick,
}: {
  active: boolean;
  unlocked: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={tapScale}
      transition={springQuick}
      aria-label={unlocked ? 'Stellar' : 'Stellar (locked)'}
      aria-pressed={active}
      className={cn(
        'aspect-square rounded-pill inline-flex items-center justify-center shadow-sm',
        unlocked ? '' : 'opacity-60',
      )}
      style={{
        backgroundColor: unlocked
          ? `rgb(${stellarAccent.light})`
          : 'rgb(var(--surface-secondary))',
      }}
    >
      {unlocked ? (
        active ? (
          <Check size={14} strokeWidth={3} className="text-white" />
        ) : null
      ) : (
        <Lock
          size={12}
          strokeWidth={2}
          className="text-text-tertiary"
        />
      )}
    </motion.button>
  );
}
